import { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from '../../../i18n/translations';

interface NavbarProps {
  isScrolled: boolean;
}

export default function Navbar({ isScrolled }: NavbarProps) {
  const { language, setLanguage } = useLanguage();
  const t = translations[language].navbar;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/92677d645ff7770f827851cf36daa564.png"
              alt="Logo"
              className="h-12 w-auto object-contain"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a
              href="#about"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-[#00A3FF]' : 'text-white hover:text-[#00A3FF]'
              }`}
            >
              {t.about}
            </a>
            <a
              href="#services"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-[#00A3FF]' : 'text-white hover:text-[#00A3FF]'
              }`}
            >
              {t.services}
            </a>
            <a
              href="#why-us"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-[#00A3FF]' : 'text-white hover:text-[#00A3FF]'
              }`}
            >
              {t.whyUs}
            </a>
            <a
              href="#showcase"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-[#00A3FF]' : 'text-white hover:text-[#00A3FF]'
              }`}
            >
              {t.showcase}
            </a>
            <a
              href="#contact"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-[#00A3FF]' : 'text-white hover:text-[#00A3FF]'
              }`}
            >
              {t.contact}
            </a>

            {/* Language Switcher */}
            <div className={`flex items-center rounded-lg border overflow-hidden transition-all ${
              isScrolled ? 'border-[#00A3FF]' : 'border-white'
            }`}>
              <button
                onClick={() => setLanguage('zh')}
                className={`px-3 py-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  language === 'zh'
                    ? isScrolled
                      ? 'bg-[#00A3FF] text-white'
                      : 'bg-white text-[#00A3FF]'
                    : isScrolled
                    ? 'text-[#00A3FF] hover:bg-[#00A3FF] hover:text-white'
                    : 'text-white hover:bg-white hover:text-[#00A3FF]'
                }`}
              >
                中文
              </button>
              <div className={`w-px h-5 ${isScrolled ? 'bg-[#00A3FF]' : 'bg-white'}`}></div>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  language === 'en'
                    ? isScrolled
                      ? 'bg-[#00A3FF] text-white'
                      : 'bg-white text-[#00A3FF]'
                    : isScrolled
                    ? 'text-[#00A3FF] hover:bg-[#00A3FF] hover:text-white'
                    : 'text-white hover:bg-white hover:text-[#00A3FF]'
                }`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3 bg-white rounded-lg shadow-lg p-4">
            <a
              href="#about"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-medium text-gray-700 hover:text-[#00A3FF] transition-colors cursor-pointer py-2"
            >
              {t.about}
            </a>
            <a
              href="#services"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-medium text-gray-700 hover:text-[#00A3FF] transition-colors cursor-pointer py-2"
            >
              {t.services}
            </a>
            <a
              href="#why-us"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-medium text-gray-700 hover:text-[#00A3FF] transition-colors cursor-pointer py-2"
            >
              {t.whyUs}
            </a>
            <a
              href="#showcase"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-medium text-gray-700 hover:text-[#00A3FF] transition-colors cursor-pointer py-2"
            >
              {t.showcase}
            </a>
            <a
              href="#contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-medium text-gray-700 hover:text-[#00A3FF] transition-colors cursor-pointer py-2"
            >
              {t.contact}
            </a>

            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-center space-x-1 pt-3 border-t border-gray-200">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLanguage('zh')}
                  className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    language === 'zh'
                      ? 'bg-[#00A3FF] text-white'
                      : 'text-gray-600 hover:text-[#00A3FF] hover:bg-gray-50'
                  }`}
                >
                  中文
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    language === 'en'
                      ? 'bg-[#00A3FF] text-white'
                      : 'text-gray-600 hover:text-[#00A3FF] hover:bg-gray-50'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}